import { FlexBox, Icon, Page, Spinner } from '@dixa/lib-shared-habla';
import { useCallback } from 'react';

import { aiApi } from 'apis/ai/ai.api';
import { isDetailedError } from 'apis/ai/types/aiStyleGuides.types';
import { AppLink } from 'components/appLink';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import {
  SettingsAIOptionsStyleGuideForm,
  SettingsAIOptionsStyleGuideFormValues,
} from '../../components/form/styleGuideForm';

export const SettingsAIOptionsStyleGuideEditView = (): JSX.Element => {
  const { styleguideId } = useAppPathParams(
    '/settings/ai/style-guide/:styleguideId',
  );

  const [editStyleGuide] = aiApi.useUpdateAIWritingStyleGuideMutation();
  const notify = useNotifications();

  const { styleGuide, isFetching } = aiApi.useReadAIWritingStyleGuideQuery(
    { styleguideId },
    {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        styleGuide: data?.data,
      }),
      refetchOnMountOrArgChange: true,
    },
  );

  const handleEdit = useCallback(
    async (formData: SettingsAIOptionsStyleGuideFormValues) => {
      try {
        await editStyleGuide({
          styleguideId,
          ...formData,
          ...(formData.contexts.channels.length > 0 && {
            contexts: {
              channels: formData.contexts.channels,
              features: ['impr_reply'],
            },
          }),
        }).unwrap();

        notify(
          {
            title: 'The writing style guide has been edited',
            icon: 'done',
            time: 2500,
            leftAction: {
              text: 'Go back to writing style guides overview',
              action: {
                navigate: '/settings/ai/style-guide',
              },
            },
          },
          NotificationType.Success,
        );
      } catch (error) {
        if (
          isDetailedError(error) &&
          error.data.detail === 'duplicate_context'
        ) {
          return notify(
            {
              title: 'Failed to edit writing style guide',
              text: 'Duplicate channels not allowed. Please ensure that the selected channel(s) does not exist in another writing style guide.',
              icon: 'exclamation',
              time: 7000,
            },
            NotificationType.Error,
          );
        }
        notify(
          {
            title: 'Failed to edit writing style guide',
            icon: 'exclamation',
            time: 2500,
          },
          NotificationType.Error,
        );
      }
    },
    [editStyleGuide, notify, styleguideId],
  );

  return (
    <Page>
      <Page.Head
        title={
          <FlexBox gap="200" alignItems="center">
            <AppLink
              path="/settings/ai/style-guide"
              style={{ color: 'inherit' }}
            >
              Writing style guides
            </AppLink>
            <Icon name="angle-right" />
            {styleGuide?.name}
          </FlexBox>
        }
      />

      <Page.Body>
        {isFetching ? (
          <FlexBox height="100%" alignItems="center" justifyContent="center">
            <Spinner />
          </FlexBox>
        ) : (
          <SettingsAIOptionsStyleGuideForm
            styleGuide={styleGuide}
            onSubmit={handleEdit}
          />
        )}
      </Page.Body>
    </Page>
  );
};
